import React, { useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { X } from 'lucide-react';
import { useKeyObjects } from '../../hooks/keyRegister';
import { KEY_OBJECT_GROUPS, KeyObject, KeyObjectRef } from './keyObjects';

interface BuildingOption {
    key: string;
    name: string;
}

interface KeyTypeObjectsFieldProps {
    value: KeyObjectRef[];
    onChange: (value: KeyObjectRef[]) => void;
    buildings: BuildingOption[];
}

/** Show a search field above a building's objects once the list gets long. */
const OBJECT_SEARCH_THRESHOLD = 12;

const sameObject = (a: KeyObjectRef, b: KeyObjectRef): boolean => a.kind === b.kind && a.key === b.key;

/**
 * "Lägenheter, lokaler och förråd" in the key type modal: the picked objects as chips across
 * buildings, and a building dropdown that lists that building's objects as checkboxes. Only the
 * chosen building and the buildings of picked objects are loaded. Independent of the building
 * checkboxes: a type can open a whole building, objects, or both.
 */
const KeyTypeObjectsField: React.FC<KeyTypeObjectsFieldProps> = ({ value, onChange, buildings }) => {
    const [pickerBuilding, setPickerBuilding] = useState<string>(() => value[0]?.buildingKey ?? '');
    const [search, setSearch] = useState('');

    const neededBuildings = useMemo(
        () => Array.from(new Set([pickerBuilding, ...value.map(o => o.buildingKey)].filter(Boolean))),
        [pickerBuilding, value]
    );
    const { objectsByBuilding, loadingBuildings } = useKeyObjects(neededBuildings);

    const buildingName = (buildingKey: string) => buildings.find(b => b.key === buildingKey)?.name ?? 'Okänd byggnad';
    const objectName = (ref: KeyObjectRef): string => {
        const objects = objectsByBuilding[ref.buildingKey];
        if (!objects) return '…';
        return objects.find(o => sameObject(o, ref))?.name ?? 'Borttaget objekt';
    };

    const toggle = (object: KeyObject) => {
        const ref: KeyObjectRef = { kind: object.kind, key: object.key, buildingKey: object.buildingKey };
        onChange(value.some(v => sameObject(v, ref)) ? value.filter(v => !sameObject(v, ref)) : [...value, ref]);
    };

    const pickerObjects = pickerBuilding ? objectsByBuilding[pickerBuilding] : undefined;
    const needle = search.trim().toLocaleLowerCase('sv');
    const visibleObjects = (pickerObjects ?? []).filter(o => !needle || o.name.toLocaleLowerCase('sv').includes(needle));

    return (
        <Form.Group className="mb-3">
            <Form.Label>Lägenheter, lokaler och förråd</Form.Label>
            {value.length > 0 && (
                <div className="key-type-object-chips">
                    {value.map(ref => {
                        const label = `${buildingName(ref.buildingKey)} · ${objectName(ref)}`;
                        return (
                            <span key={`${ref.kind}/${ref.key}`} className="key-type-object-chip">
                                {label}
                                <Button variant="link" size="sm" className="p-0" aria-label={`Ta bort ${label}`} onClick={() => onChange(value.filter(v => !sameObject(v, ref)))}>
                                    <X size={14} aria-hidden="true" />
                                </Button>
                            </span>
                        );
                    })}
                </div>
            )}
            <Form.Select
                aria-label="Byggnad för lägenheter, lokaler och förråd"
                value={pickerBuilding}
                onChange={e => {
                    setPickerBuilding(e.target.value);
                    setSearch('');
                }}
            >
                <option value="">Välj byggnad…</option>
                {buildings.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
            </Form.Select>
            {pickerBuilding && (
                loadingBuildings[pickerBuilding] && !pickerObjects ? (
                    <div className="text-muted mt-2"><Spinner animation="border" size="sm" className="me-2" />Hämtar…</div>
                ) : !pickerObjects ? (
                    <div className="text-danger mt-2">Kunde inte hämta byggnadens lägenheter, lokaler och förråd.</div>
                ) : pickerObjects.length === 0 ? (
                    <div className="text-muted mt-2">Byggnaden har inga lägenheter, lokaler eller förråd.</div>
                ) : (
                    <>
                        {pickerObjects.length > OBJECT_SEARCH_THRESHOLD && (
                            <Form.Control
                                className="mt-2"
                                placeholder="Sök"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                aria-label="Sök lägenhet, lokal eller förråd"
                            />
                        )}
                        <div className="key-type-buildings mt-2">
                            {KEY_OBJECT_GROUPS.map(group => {
                                const inGroup = visibleObjects.filter(o => o.group === group);
                                if (inGroup.length === 0) return null;
                                return (
                                    <div key={group}>
                                        <div className="key-type-object-group">{group}</div>
                                        {inGroup.map(object => (
                                            <Form.Check
                                                key={object.key}
                                                type="checkbox"
                                                id={`key-type-object-${object.key}`}
                                                label={object.name}
                                                checked={value.some(v => sameObject(v, object))}
                                                onChange={() => toggle(object)}
                                            />
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )
            )}
            <Form.Text className="text-muted">För nycklar till en enskild lägenhet, lokal eller ett förråd.</Form.Text>
        </Form.Group>
    );
};

export default KeyTypeObjectsField;
